import React, { useState, useEffect } from 'react';
import portalAPI from '../services/portalAPI';

interface Terminal {
  id: number;
  code: string;
  name: string;
  city: string;
  state: string;
  address: string;
  phone: string;
  region?: string;
  zip_code?: string;
}

interface CarrierGroup {
  carrier_code: string;
  carrier_name: string;
  is_primary: boolean;
  terminals: Terminal[];
}

interface TerminalSelectorProps {
  value: number | null;
  onChange: (terminalId: number | null) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const TerminalSelector: React.FC<TerminalSelectorProps> = ({
  value,
  onChange,
  required = false,
  disabled = false,
  className = '',
  style = {},
}) => {
  const [carriers, setCarriers] = useState<CarrierGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTerminals = async () => {
      try {
        setLoading(true);
        const response = await portalAPI.get('/shipping/terminals/?grouped=true');
        if (response.data?.carriers) {
          setCarriers(response.data.carriers);
        }
      } catch (err) {
        console.error('Error fetching terminals:', err);
        setError('Could not load pickup terminals');
      } finally {
        setLoading(false);
      }
    };

    fetchTerminals();
  }, []);

  const formatPhone = (phone: string) => {
    // Format phone for display if needed
    return phone || '';
  };

  const formatTerminalOption = (terminal: Terminal) => {
    const parts = [terminal.name];
    if (terminal.city && terminal.state) {
      parts.push(`${terminal.city}, ${terminal.state}`);
    }
    if (terminal.phone) {
      parts.push(`(${formatPhone(terminal.phone)})`);
    }
    return parts.join(' - ');
  };

  if (loading) {
    return (
      <select disabled style={style} className={className}>
        <option>Loading terminals...</option>
      </select>
    );
  }

  if (error) {
    return (
      <select disabled style={style} className={className}>
        <option>Error loading terminals</option>
      </select>
    );
  }

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
      required={required}
      disabled={disabled}
      style={style}
      className={className}
    >
      <option value="">-- Select Pickup Terminal --</option>
      {carriers.map((carrier) => (
        <optgroup
          key={carrier.carrier_code}
          label={`${carrier.carrier_name.toUpperCase()} ${carrier.is_primary ? '(Direct - Faster)' : '(Partner Carrier)'}`}
        >
          {carrier.terminals.map((terminal) => (
            <option key={terminal.id} value={terminal.id}>
              {formatTerminalOption(terminal)}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};

export default TerminalSelector;
